import React, { useEffect, useState } from "react";
import { Card, Table, Button, Modal, Form, DatePicker, InputNumber, Input, Space, message, Popconfirm, Calendar, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageTransition from "../../components/ui/PageTransition";
import PageHeader from "../../components/ui/PageHeader";
import StatusTag from "../../components/ui/StatusTag";
import EmptyState from "../../components/ui/EmptyState";
import ViewToggle from "../../components/ui/ViewToggle";
import { CalendarHeader } from "../Attendance/MyMonthTab";

const OT_STATUS_COLORS = { pending: "#F59E0B", approved: "#14B8A6", rejected: "#EF4444" };

const Overtime = () => {
  const { request } = useRequest();
  const location = useLocation();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [view, setView] = useState("list");
  const [submitting, setSubmitting] = useState(false);
  const [actualHoursRecord, setActualHoursRecord] = useState(null);
  const [actualHoursSubmitting, setActualHoursSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [actualHoursForm] = Form.useForm();

  const fetchMine = () => {
    request({ url: apiPath.overtimeMine, method: "GET", onSuccess: (res) => setRequests(res?.data?.data || []) });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fetchMine, []);

  const openCreate = () => {
    form.resetFields();
    setSubmitting(false);
    setModalOpen(true);
  };

  // "Request OT" on the Home screen links here with { autoOpen: true } so
  // the form opens immediately instead of landing on a bare list — clear
  // the state right after so a back/forward navigation or refresh doesn't
  // reopen it unexpectedly.
  useEffect(() => {
    if (location.state?.autoOpen) {
      openCreate();
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const onSubmit = () => {
    // Without this guard, a slow request + no visible feedback on the OK
    // button led to users clicking it repeatedly, creating duplicates.
    if (submitting) return;
    form.validateFields().then((values) => {
      setSubmitting(true);
      request({
        url: apiPath.overtime,
        method: "POST",
        data: {
          date: values.date.format("YYYY-MM-DD"),
          hours: values.hours,
          reason: values.reason,
        },
        onSuccess: () => {
          setSubmitting(false);
          message.success("Overtime request submitted");
          setModalOpen(false);
          fetchMine();
        },
        onError: (err) => {
          setSubmitting(false);
          message.error(err?.response?.data?.message || "Could not submit overtime request");
        },
      });
    });
  };

  const onCancel = (record) => {
    request({
      url: `${apiPath.overtime}/${record._id}/cancel`,
      method: "PUT",
      onSuccess: () => {
        message.success("Overtime request cancelled");
        fetchMine();
      },
    });
  };

  const todayStr = moment().format("YYYY-MM-DD");
  // Approved OT whose date has passed and hasn't had actual hours logged
  // yet — the same condition the backend uses to allow the PUT and to
  // decide who gets the daily reminder notification.
  const needsActualHours = (record) => record.status === "approved" && record.date < todayStr && record.actualHours == null;

  const openActualHours = (record) => {
    setActualHoursRecord(record);
    actualHoursForm.resetFields();
    setActualHoursSubmitting(false);
  };

  const onSubmitActualHours = () => {
    if (actualHoursSubmitting) return;
    actualHoursForm.validateFields().then((values) => {
      setActualHoursSubmitting(true);
      request({
        url: `${apiPath.overtime}/${actualHoursRecord._id}/actual-hours`,
        method: "PUT",
        data: { actualHours: values.actualHours },
        onSuccess: () => {
          setActualHoursSubmitting(false);
          message.success("Actual hours logged");
          setActualHoursRecord(null);
          fetchMine();
        },
        onError: (err) => {
          setActualHoursSubmitting(false);
          message.error(err?.response?.data?.message || "Could not log actual hours");
        },
      });
    });
  };

  const columns = [
    { title: "Date", dataIndex: "date" },
    { title: "Expected Hours", dataIndex: "hours" },
    { title: "Actual Hours", dataIndex: "actualHours", render: (v) => (v != null ? v : "—") },
    { title: "Reason", dataIndex: "reason", ellipsis: true },
    { title: "Status", dataIndex: "status", render: (s) => <StatusTag status={s} /> },
    { title: "Comment", dataIndex: "adminComment" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          {record.status === "pending" && (
            <Popconfirm title="Cancel this overtime request?" onConfirm={() => onCancel(record)}>
              <Button size="small" danger>
                Cancel
              </Button>
            </Popconfirm>
          )}
          {needsActualHours(record) && (
            <Button size="small" type="primary" onClick={() => openActualHours(record)}>
              Log Actual Hours
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const overtimeDateCellRender = (value) => {
    const dateStr = value.format("YYYY-MM-DD");
    const matches = requests.filter((r) => r.date === dateStr);
    const isPast = !value.isSameOrAfter(moment(), "day");

    return (
      <div className="i-ems-calendar-cell-content">
        {matches.map((r) => {
          const color = OT_STATUS_COLORS[r.status] || "#9CA3AF";
          const displayHours = r.actualHours != null ? r.actualHours : r.hours;
          return (
            <Tooltip key={r._id} title={`Overtime (${displayHours}h${r.actualHours != null ? " actual" : " expected"}) — ${r.status}`}>
              <div className="i-ems-calendar-tag" style={{ color, background: `${color}1A` }}>
                OT {displayHours}h
              </div>
            </Tooltip>
          );
        })}
        {!isPast && (
          <div className="i-ems-calendar-apply-wrap">
            <Button
              size="small"
              type="primary"
              className="i-ems-calendar-apply-btn"
              icon={<PlusOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                openCreate();
                form.setFieldsValue({ date: value });
              }}
            >
              Apply
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <PageTransition>
      <PageHeader
        title="Overtime"
        subtitle="Request overtime for upcoming work"
        actions={<ViewToggle value={view} onChange={setView} />}
      />
      {view === "list" ? (
        <Card>
          <Space style={{ marginBottom: 16, width: "100%", justifyContent: "flex-end" }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Request Overtime
            </Button>
          </Space>
          <div style={{ overflowX: "auto" }}>
            <Table
              rowKey="_id"
              columns={columns}
              dataSource={requests}
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: <EmptyState message="No overtime requests yet" actionLabel="Request Overtime" onAction={openCreate} /> }}
            />
          </div>
        </Card>
      ) : (
        <Card>
          <div className="i-ems-apply-calendar">
            <Calendar dateCellRender={overtimeDateCellRender} headerRender={({ value: v, onChange }) => <CalendarHeader value={v} onChange={onChange} />} />
          </div>
        </Card>
      )}

      <Modal
        title="Request Overtime"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={onSubmit}
        confirmLoading={submitting}
        okButtonProps={{ disabled: submitting }}
        cancelButtonProps={{ disabled: submitting }}
        destroyOnClose
      >
        <Form layout="vertical" form={form}>
          <Form.Item name="date" label="Date" rules={[{ required: true }]}>
            <DatePicker
              style={{ width: "100%" }}
              disabledDate={(current) => current && current < moment().startOf("day")}
            />
          </Form.Item>
          <Form.Item
            name="hours"
            label="Expected Hours"
            rules={[{ required: true }]}
            extra="Minimum 4 hours per overtime request. You'll log your actual hours worked once the date has passed."
          >
            <InputNumber style={{ width: "100%" }} min={4} max={12} step={0.5} />
          </Form.Item>
          <Form.Item name="reason" label="Reason" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Log Actual Overtime Hours"
        open={!!actualHoursRecord}
        onCancel={() => setActualHoursRecord(null)}
        onOk={onSubmitActualHours}
        confirmLoading={actualHoursSubmitting}
        okButtonProps={{ disabled: actualHoursSubmitting }}
        cancelButtonProps={{ disabled: actualHoursSubmitting }}
        destroyOnClose
      >
        {actualHoursRecord && (
          <p style={{ color: "#8C8C8C", marginBottom: 16 }}>
            You requested <b>{actualHoursRecord.hours}h</b> overtime on <b>{actualHoursRecord.date}</b>. How many hours did you actually
            work? This is what will be used for your payslip.
          </p>
        )}
        <Form layout="vertical" form={actualHoursForm}>
          <Form.Item name="actualHours" label="Actual Hours Worked" rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} min={0} max={24} step={0.5} />
          </Form.Item>
        </Form>
      </Modal>
    </PageTransition>
  );
};

export default Overtime;
